/**
 * Member query hooks
 *
 * Provides React Query hooks for fetching member data.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase, handleSupabaseError, handleUnexpectedError, fetchAllRows } from '@/lib';
import { Member } from '@/types';
import { MEMBER_QUERY_KEYS } from '@/constants';

/**
 * Hook to fetch all members
 *
 * @param includeDeleted - Whether to include soft-deleted members (default: false)
 * @returns Query result with array of members
 */
export function useMembers(includeDeleted = false) {
  return useQuery({
    queryKey: [MEMBER_QUERY_KEYS.ALL, includeDeleted],
    queryFn: async () => {
      try {
        // Paged (fetchAllRows) so the member directory never silently
        // truncates at PostgREST's 1,000-row cap as the roster grows.
        const { data, error } = await fetchAllRows<Member>(() => {
          let query = supabase
            .from('members')
            .select('*')
            .order('created_at', { ascending: false });

          // By default, exclude soft deleted members
          if (!includeDeleted) {
            query = query.filter('deleted_at', 'is', null);
          }

          return query;
        });

        if (error) handleSupabaseError(error, { operation: 'fetching members' });

        return data;
      } catch (error) {
        handleUnexpectedError(error, { hookName: 'useMembers', operation: 'loading members' });
      }
    },
  });
}

/**
 * Hook to fetch a single member by ID
 *
 * @param id - The member's UUID
 * @returns Query result with member data
 */
export function useMember(id: string) {
  return useQuery({
    queryKey: MEMBER_QUERY_KEYS.BY_ID(id),
    queryFn: async () => {
      if (!id) {
        throw new Error('Member ID is required');
      }

      try {
        const { data, error } = await supabase
          .from('members')
          .select('*')
          .eq('id', id)
          .is('deleted_at', null)  // Exclude soft-deleted members
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            throw new Error('Member not found or has been deleted');
          }
          handleSupabaseError(error, { operation: 'fetching member', params: { memberId: id } });
        }

        if (!data) {
          throw new Error('Member not found');
        }

        return data as Member;
      } catch (error) {
        handleUnexpectedError(error, { hookName: 'useMember', operation: 'loading member details', params: { memberId: id } });
      }
    },
    enabled: !!id, // Disable query when ID is empty
  });
}

/**
 * Hook for fetching deleted members (admin only)
 *
 * @returns Query result with array of soft-deleted members
 */
export function useDeletedMembers() {
  return useQuery({
    queryKey: [MEMBER_QUERY_KEYS.DELETED],
    queryFn: async () => {
      try {
        // Paged (fetchAllRows) so the list never silently truncates at
        // PostgREST's 1,000-row cap.
        const { data, error } = await fetchAllRows<Member>(() =>
          supabase
            .from('members')
            .select('*')
            .not('deleted_at', 'is', null)
            .order('deleted_at', { ascending: false }),
        );

        if (error) handleSupabaseError(error, { operation: 'fetching deleted members' });

        return data;
      } catch (error) {
        handleUnexpectedError(error, { hookName: 'useDeletedMembers', operation: 'loading deleted members' });
      }
    },
  });
}
